"use client";

import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  HandCoinsIcon,
  Link2Icon,
  SearchIcon,
  ShieldCheckIcon,
} from "lucide-react";

import { Container } from "@/app/(marketing)/_components/ui/container";

interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
  highlights: string[];
}

const features: Feature[] = [
  {
    icon: ShieldCheckIcon,
    title: "Data you can publish",
    description:
      "Every participant is identity-verified and screened by AI before they reach your study, so your grant goes toward insight, not toward cleaning junk responses.",
    highlights: [
      "Identity, credential, and location verification",
      "Attention and engagement monitoring",
      "Bot and AI-generated answer detection",
    ],
  },
  {
    icon: SearchIcon,
    title: "Reach the exact sample you need",
    description:
      "Describe your population in plain language and screen for niche, hard-to-reach groups. B2B and B2C, students to specialists.",
    highlights: [
      "Natural-language audience targeting",
      "Custom screeners and attestation filters",
      "Set quotas and balance your sample",
    ],
  },
  {
    icon: HandCoinsIcon,
    title: "Fair pay, honest effort",
    description:
      "Participants earn at least a fair minimum reward on every study, shown to them upfront. Ethical compensation keeps people motivated and your data trustworthy.",
    highlights: [
      "Minimum reward enforced on every study",
      "Transparent pay before participants start",
      "Supports your IRB and ethics requirements",
    ],
  },
  {
    icon: Link2Icon,
    title: "Fits your existing tools",
    description:
      "Connect surveys and experiments from Qualtrics, Gorilla, SoSci, or any tool with a single link. Or build Terac into your pipeline with our API.",
    highlights: [
      "Link-based studies with completion codes",
      "REST API for automated workflows",
      "Re-contact participants for multi-wave studies",
    ],
  },
];

export default function AcademicResearchFeatures() {
  return (
    <section className="py-20">
      <Container>
        {/* Section header */}
        <div className="mb-16 flex flex-col items-center gap-8">
          <div className="bg-marketing-card-blue rounded-t-[10px] px-2.5 py-1.5">
            <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
              For academics
            </p>
          </div>
          <h2 className="text-marketing-text dark:text-marketing-cream max-w-[700px] text-center text-3xl leading-[1.1] font-medium tracking-[-1.5px] md:text-4xl lg:text-[50px]">
            Built for research you can stand behind
          </h2>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, transform: "translateY(20px)" }}
              whileInView={{ opacity: 1, transform: "translateY(0px)" }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="border-marketing-border bg-marketing-cream-light rounded-[20px] border p-8"
            >
              <div className="bg-marketing-card-blue mb-4 flex h-12 w-12 items-center justify-center rounded-full">
                <feature.icon className="text-marketing-blue h-6 w-6" />
              </div>
              <h3 className="text-marketing-text mb-3 text-xl font-medium">
                {feature.title}
              </h3>
              <p className="text-marketing-text-muted mb-6 text-base leading-relaxed">
                {feature.description}
              </p>
              <div className="flex flex-col gap-2">
                {feature.highlights.map((highlight) => (
                  <div key={highlight} className="flex items-center gap-2.5">
                    <div className="bg-marketing-blue h-1.5 w-1.5 shrink-0 rounded-full" />
                    <p className="text-marketing-text text-sm font-medium">
                      {highlight}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}
